import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { safeHtml } from "@/lib/sanitize";
import { ArrowLeft, Calendar, Tag } from "lucide-react";

export function BlogPage() {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/blog", { params: { published_only: true } }).then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12" data-testid="blog-page">
      <div className="mb-10">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-600 mb-3">Blog</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight text-ink-900">
          Rehberler, İpuçları ve <span className="gradient-text">Trendler</span>
        </h1>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.map((p) => (
          <Link
            key={p.id}
            to={`/blog/${p.slug}`}
            data-testid={`blog-card-${p.slug}`}
            className="group rounded-3xl overflow-hidden border border-ink-100 bg-white hover:brand-shadow hover:-translate-y-1 transition-transform"
          >
            <div className="aspect-[16/10] overflow-hidden">
              <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 text-[11px] text-ink-500">
                <span className="inline-flex items-center gap-1"><Tag className="h-3 w-3" /> {p.category}</span>
                <span>·</span>
                <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(p.published_at).toLocaleDateString("tr-TR")}</span>
              </div>
              <h3 className="mt-2 font-display font-bold text-lg text-ink-900 group-hover:text-brand-600">{p.title}</h3>
              <p className="mt-2 text-sm text-ink-500 line-clamp-3">{p.excerpt}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function BlogDetailPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost({}));
    api.get("/blog", { params: { published_only: true } }).then((r) => setRelated(r.data.filter((x) => x.slug !== slug).slice(0, 3)));
  }, [slug]);

  if (!post) return <div className="p-12 text-center">Yükleniyor...</div>;
  if (!post.title) return <div className="p-12 text-center">Yazı bulunamadı.</div>;

  return (
    <article className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-12" data-testid="blog-detail-page">
      <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-600 mb-6"><ArrowLeft className="h-4 w-4" /> Blog'a Dön</Link>
      <div className="text-[10px] font-bold uppercase tracking-widest text-brand-600">{post.category}</div>
      <h1 className="mt-2 font-display text-4xl md:text-5xl font-black tracking-tight text-ink-900">{post.title}</h1>
      <div className="mt-3 text-sm text-ink-500">{new Date(post.published_at).toLocaleDateString("tr-TR")} · {post.author}</div>
      {post.cover_image && (
        <div className="mt-8 aspect-[16/10] overflow-hidden rounded-3xl">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="mt-8 prose prose-lg max-w-none prose-p:text-ink-500 prose-headings:font-display prose-a:text-brand-600" dangerouslySetInnerHTML={safeHtml(post.content)} />

      {related.length > 0 && (
        <div className="mt-16">
          <h3 className="font-display font-bold text-2xl text-ink-900 mb-6">İlgili Yazılar</h3>
          <div className="grid sm:grid-cols-3 gap-5">
            {related.map((r) => (
              <Link key={r.id} to={`/blog/${r.slug}`} className="group rounded-2xl overflow-hidden border border-ink-100 bg-white">
                <div className="aspect-video overflow-hidden">
                  <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                </div>
                <div className="p-4">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-brand-600">{r.category}</div>
                  <div className="mt-1 font-semibold text-sm text-ink-900 group-hover:text-brand-600 line-clamp-2">{r.title}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
